import type { CertificateProductFormMode, CertificateProductFormValues } from "@/pages/platform/certificate-product-form-types";
import type {
  CertificateProductPriceRow,
  UpsertCertificateProductPriceBody,
} from "@/services/platformCertificatePricingService";
import { isValidCertificateProductTargetSlug } from "@/utils/certificateProductSlug";

export function minorToPesoInput(minor: number): string {
  return (minor / 100).toFixed(2);
}

export function pesoInputToMinor(raw: string): number | null {
  const n = parseFloat(raw.replace(",", ".").replace(/\s/g, ""));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export type ParseCertificateProductUpsertResult =
  | { ok: true; body: UpsertCertificateProductPriceBody }
  | { ok: false; errorKey: string };

/**
 * Maps form state to upsert body. `editingRow` must be set when `mode === "edit"`.
 */
export function parseCertificateProductFormToUpsert(
  form: CertificateProductFormValues,
  mode: CertificateProductFormMode,
  editingRow: CertificateProductPriceRow | null,
): ParseCertificateProductUpsertResult {
  const targetType =
    mode === "edit" && editingRow ? editingRow.targetType : form.targetType.trim();
  if (targetType === "") {
    return { ok: false, errorKey: "platform.products.selectTargetTypeError" };
  }
  if (mode === "add" && !isValidCertificateProductTargetSlug(targetType)) {
    return { ok: false, errorKey: "platform.products.invalidTargetSlug" };
  }
  const displayName = form.displayName.trim();
  if (displayName === "") {
    return { ok: false, errorKey: "platform.products.displayNameRequired" };
  }
  if (!form.involvesAccountant && !form.involvesNotary) {
    return { ok: false, errorKey: "platform.products.channelRequired" };
  }
  if (form.fulfillmentModes.length === 0) {
    return { ok: false, errorKey: "platform.products.fulfillmentModesRequired" };
  }

  const fixedCostItems: Array<{ label: string; amountMinor: number; includesIva: boolean }> = [];
  for (const item of form.fixedCostItems) {
    const minor = pesoInputToMinor(item.pesos);
    if (minor == null) {
      return { ok: false, errorKey: "platform.products.invalidFixed" };
    }
    if (minor > 0 || item.label.trim() !== "") {
      fixedCostItems.push({
        label: item.label.trim(),
        amountMinor: minor,
        includesIva: item.includesIva,
      });
    }
  }
  const fixedAmountMinor = fixedCostItems.reduce((sum, item) => sum + item.amountMinor, 0);

  const ur = parseFloat(form.urUnits.replace(",", "."));
  if (!Number.isFinite(ur) || ur < 0) {
    return { ok: false, errorKey: "platform.products.invalidUrUnits" };
  }
  const margin = parseFloat(form.marginPercent.replace(",", "."));
  if (!Number.isFinite(margin) || margin < 0) {
    return { ok: false, errorKey: "platform.products.invalidMargin" };
  }
  const perFileMinor = pesoInputToMinor(form.perFileStoragePesos);
  if (perFileMinor == null) {
    return { ok: false, errorKey: "platform.products.invalidPerFileStorage" };
  }
  const deliveryFeeMinor = pesoInputToMinor(form.deliveryFeePesos);
  if (deliveryFeeMinor == null) {
    return { ok: false, errorKey: "platform.products.invalidDeliveryFee" };
  }
  const professionalStampMinor = pesoInputToMinor(form.professionalStampPesos);
  if (professionalStampMinor == null) {
    return { ok: false, errorKey: "platform.products.invalidProfessionalStamp" };
  }
  const consumerServiceFeeMinor = pesoInputToMinor(form.consumerServiceFeePesos);
  if (consumerServiceFeeMinor == null) {
    return { ok: false, errorKey: "platform.products.invalidConsumerServiceFee" };
  }
  const sortParsed = parseInt(form.sortOrder.replace(/\s/g, ""), 10);
  const sortOrder = Number.isFinite(sortParsed) && sortParsed >= 0 ? sortParsed : 0;

  return {
    ok: true,
    body: {
      targetType,
      displayName,
      fixedAmountMinor,
      fixedCostItems: fixedCostItems.length > 0 ? fixedCostItems : null,
      urUnits: ur,
      urType: form.urType,
      marginPercent: margin,
      perFileStorageMinor: perFileMinor,
      deliveryFeeMinor,
      professionalStampMinor,
      consumerServiceFeeMinor,
      involvesAccountant: form.involvesAccountant,
      involvesNotary: form.involvesNotary,
      currency: form.currency.trim() || "UYU",
      isActive: form.active,
      sortOrder,
      requiredDocumentTypes: form.requiredDocs.length > 0 ? form.requiredDocs : null,
      fulfillmentModesAllowed: form.fulfillmentModes.length > 0 ? form.fulfillmentModes : null,
      defaultBusinessDays: (() => {
        const n = parseInt(form.defaultBusinessDays.replace(/\s/g, ""), 10);
        return Number.isFinite(n) && n >= 0 ? n : 0;
      })(),
      description: form.description.trim() === "" ? null : form.description.trim(),
      urVariableIncludesIva: form.urVariableIncludesIva,
      perFileStorageIncludesIva: form.perFileStorageIncludesIva,
      deliveryFeeIncludesIva: form.deliveryFeeIncludesIva,
      marginIncludesIva: form.marginIncludesIva,
    },
  };
}

export function certificateProductRowToUpsertBody(
  row: CertificateProductPriceRow,
): UpsertCertificateProductPriceBody {
  const ur = parseFloat(row.urUnits);
  const margin = parseFloat(row.marginPercent);
  return {
    targetType: row.targetType,
    displayName: row.displayName,
    fixedAmountMinor: row.fixedAmountMinor,
    fixedCostItems: row.fixedCostItems,
    urUnits: Number.isFinite(ur) && ur >= 0 ? ur : 0,
    urType: row.urType === "arancel" ? "arancel" : "dgi",
    marginPercent: Number.isFinite(margin) && margin >= 0 ? margin : 0,
    perFileStorageMinor: row.perFileStorageMinor ?? 0,
    deliveryFeeMinor: row.deliveryFeeMinor ?? 0,
    professionalStampMinor: row.professionalStampMinor ?? 0,
    consumerServiceFeeMinor: row.consumerServiceFeeMinor ?? 0,
    involvesAccountant: row.involvesAccountant,
    involvesNotary: row.involvesNotary,
    currency: row.currency?.trim() || "UYU",
    isActive: row.isActive,
    sortOrder: row.sortOrder ?? 0,
    requiredDocumentTypes: row.requiredDocumentTypes,
    fulfillmentModesAllowed: row.fulfillmentModesAllowed,
    defaultBusinessDays: row.defaultBusinessDays ?? 0,
    description: row.description,
    urVariableIncludesIva: row.urVariableIncludesIva,
    perFileStorageIncludesIva: row.perFileStorageIncludesIva,
    deliveryFeeIncludesIva: row.deliveryFeeIncludesIva,
    marginIncludesIva: row.marginIncludesIva ?? false,
  };
}
